/*
 * Propuestas: versiones, estado y el paso al proyecto. Una propuesta nace del
 * cierre D5 (se prellena con lo que el Taller acordó), se comparte con el cliente,
 * y al aceptarse se convierte en proyecto y en cobros. La hoja de un solo número
 * es el documento que recibe el cliente.
 */

import { useVista } from "../../api/hooks";
import { useCambiarPropuesta } from "../../api/hooks";
import { ESTADOS_PROPUESTA, type CasoResumen, type CuentaDetalle, type Propuesta } from "../../api/tipos";
import { useAvisos } from "../../shell/Aviso";
import { useFormularios } from "../../shell/Formularios";
import { Abierto, CabeceraSeccion, Clave, fmtFecha, fmtMXN } from "../../ui/primitivas";
import type { Pestana } from "./Cuenta";

export function Propuestas({ c, caso, ir }: { c: CuentaDetalle; caso: CasoResumen | null; ir: (p: Pestana) => void }) {
  const { abrir } = useFormularios();
  const { avisar } = useAvisos();
  const { data: vista } = useVista(caso?.id ?? null);
  const cambiar = useCambiarPropuesta(c.id);
  const qs = [...c.propuestas].sort((a, b) => b.version - a.version);
  const listo = !!vista?.cierre.indicador || (caso ? caso.estado >= "D5" : false);

  const act = async (q: Propuesta, estado: "enviada" | "aceptada" | "rechazada") => {
    const r = await cambiar.mutateAsync({ id: q.id, estado });
    if (estado === "aceptada") { avisar(`«${q.nombre}» aceptada · proyecto y ${q.pagos.length + (q.riesgo_pct > 0 ? 1 : 0)} cobros creados`); ir("proyecto"); }
    else if (estado === "enviada") avisar(`Propuesta v${q.version} enviada a ${c.empresa}`);
    else avisar(`Propuesta v${q.version} marcada como rechazada`);
    return r;
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <CabeceraSeccion titulo="Propuestas" n={qs.length} derecha={<span style={{ display: "flex", gap: 8 }}>
        {caso && <a href={`/legacy/hoja.html?caso=${encodeURIComponent(caso.id)}`} target="_blank" rel="noreferrer" className="btn btn-secundario" style={{ textDecoration: "none" }}>Hoja de un solo número ↗</a>}
        <button className="btn btn-primario" onClick={() => abrir({ tipo: "propuesta", cuenta: c, casoId: caso?.id ?? null })}>+ Redactar propuesta</button>
      </span>} />
      {!listo && qs.length === 0 && (
        <Abierto style={{ marginBottom: 14 }}>La propuesta sale del cierre D5: indicador, baseline y términos acordados en el Taller. {caso ? <>Esta cuenta va en <strong>{caso.estado}</strong>; puedes redactarla antes, pero irá sin baseline firmado.</> : "Esta cuenta no ha iniciado el descubrimiento."}</Abierto>
      )}
      {listo && qs.length === 0 && (
        <div style={{ marginBottom: 14, background: "var(--verified-bg)", color: "var(--verified)", borderRadius: "var(--r-card)", padding: "12px 14px", fontSize: 13.5 }}>
          Cierre D5 con indicador acordado{vista?.cierre.indicador ? `: ${vista.cierre.indicador.kpi}` : ""}. Lista para la hoja de un solo número.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {qs.map((q) => {
          const ui = ESTADOS_PROPUESTA[q.estado];
          const fecha = q.resuelta_en ?? q.enviada_en ?? q.creada_en;
          return (
            <section key={q.id} className="tarjeta">
              <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                <span className="mono" style={{ color: "var(--faint)" }}>v{q.version}</span>
                <span className="subtitulo" style={{ flex: 1, minWidth: 200 }}>{q.nombre}</span>
                <span className="pastilla" style={{ background: ui.bg, color: ui.color }}>{ui.label}</span>
                <span className="mono" style={{ color: "var(--faint)" }}>{fmtFecha(fecha.slice(0, 10))}</span>
              </div>
              {q.encargo && <p style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 6 }}>{q.encargo}</p>}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))", gap: 12, marginTop: 12 }}>
                <Clave k="Indicador">{q.indicador}</Clave>
                <Clave k="Baseline → meta"><span className="mono">{q.baseline ?? "—"} → {q.meta ?? "—"} {q.unidad}</span></Clave>
                <Clave k="Honorarios"><span className="mono">{fmtMXN(q.base)}</span> <span style={{ color: "var(--faint)", fontSize: 12 }}>+ {q.riesgo_pct}% at-risk</span></Clave>
                <Clave k="Pagos">{q.pagos.map((r) => `${r.pct}%`).join(" · ")}</Clave>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
                {q.estado === "borrador" && <button className="btn btn-navy" disabled={cambiar.isPending} onClick={() => act(q, "enviada")}>Compartir con el cliente</button>}
                {q.estado === "enviada" && <>
                  <button className="btn" style={{ background: "var(--verified)", color: "#fff" }} disabled={cambiar.isPending} onClick={() => act(q, "aceptada")}>Cliente aceptó → crear proyecto</button>
                  <button className="btn btn-peligro" disabled={cambiar.isPending} onClick={() => act(q, "rechazada")}>Rechazada</button>
                </>}
                {q.estado === "aceptada" && <button className="btn btn-fantasma" onClick={() => ir("proyecto")}>Ver proyecto y cobros →</button>}
                {q.estado === "rechazada" && <button className="btn btn-secundario" onClick={() => abrir({ tipo: "propuesta", cuenta: c, casoId: caso?.id ?? null })}>Redactar v{qs.length + 1}</button>}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
